import { withClickId } from '@dupe/common/analyticsCommerce'
import { EXPERIMENT_ARM_PARAM } from '@dupe/common/experimentArms'
import { getExperimentArmsParam } from '~/src/lib/experimentArmSnapshot'

/** Add the current experiment arms (`xa`) at activation when the href has
 *  none. Arms are published from a client effect, so a link rendered (and
 *  memoized) before publication would otherwise go out without them. A stamp
 *  already on the href wins; the restore below removes this one too. */
function withCurrentExperimentArms(url: string, base?: string): string {
  const xa = getExperimentArmsParam()
  if (!xa) return url
  const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(url)
  const parsed = new URL(
    url,
    isAbsolute ? undefined : (base ?? 'https://dupe.com')
  )
  if (parsed.searchParams.has(EXPERIMENT_ARM_PARAM)) return url
  parsed.searchParams.set(EXPERIMENT_ARM_PARAM, xa)
  return isAbsolute
    ? parsed.toString()
    : `${parsed.pathname}${parsed.search}${parsed.hash}`
}

/**
 * Stamp a freshly minted click id onto the anchor the shopper just activated,
 * then put the original href back.
 *
 * The restore is the load-bearing half. Every product link opens `_blank`, so
 * the source page stays alive with whatever href it was left holding — and
 * React will not repaint the attribute, because the memoized value never
 * changed. A `cid` left in the DOM is then reachable by a later middle-click,
 * "open in new tab", or copied link, none of which run this handler. Each of
 * those writes ANOTHER `link_clicks` row carrying the previous click's id, and
 * the event-to-row join goes one-to-many — silently over-attributing revenue,
 * which is the exact failure per-activation minting exists to prevent.
 *
 * `setTimeout(..., 0)` is the right seam: the browser's default navigation runs
 * after event dispatch completes and before the next macrotask, so it reads the
 * stamped href, and the restore lands immediately afterwards.
 *
 * Overlapping activations are why this keeps per-anchor state instead of
 * closing over the href it happened to read. A second click landing before the
 * first restore would otherwise capture the FIRST activation's stamped href as
 * its "original", and its later restore would write that id back into the DOM
 * — reintroducing exactly the stale-cid reuse the restore exists to prevent,
 * after the first restore had already cleaned it up. So: one base href per
 * anchor, held until the last pending restore runs, and a restore that fires
 * only if the href is still the value it stamped.
 */
interface PendingStamp {
  /** The href as it was BEFORE any of the overlapping stamps touched it. */
  base: string | null
  /** The exact value the latest stamp wrote — the only thing we may undo. */
  stamped: string
  timer: ReturnType<typeof setTimeout>
}

const pending = new WeakMap<HTMLAnchorElement, PendingStamp>()

export function stampClickIdOnAnchor(
  anchor: EventTarget | null | undefined,
  clickId: string
): void {
  if (!(anchor instanceof HTMLAnchorElement)) return

  const prior = pending.get(anchor)
  // A restore is still outstanding, so the href on the element right now is
  // OUR edit, not the page's. Keep the base the first stamp captured.
  const base = prior ? prior.base : anchor.getAttribute('href')

  let stamped: string
  try {
    const origin =
      typeof window !== 'undefined' ? window.location.origin : undefined
    stamped = withCurrentExperimentArms(
      withClickId(anchor.href, clickId, origin),
      origin
    )
    anchor.setAttribute('href', stamped)
  } catch {
    return
  }

  if (prior) clearTimeout(prior.timer)

  const timer = setTimeout(() => {
    pending.delete(anchor)
    if (!anchor.isConnected) return
    // Only undo our own edit — a re-render may have legitimately replaced it,
    // and overwriting that would put a dead URL on the card.
    if (anchor.getAttribute('href') !== stamped) return
    if (base === null) anchor.removeAttribute('href')
    else anchor.setAttribute('href', base)
  }, 0)

  pending.set(anchor, { base, stamped, timer })
}
